import { Link, useNavigate } from "@tanstack/react-router";
import ArrowForward from "~icons/material-symbols/arrow-forward";
import CheckCircle from "~icons/material-symbols/check-circle";
import Info from "~icons/material-symbols/info-outline";
import { css } from "../../../../styled-system/css";
import type { R8CampaignConfig } from "../../../config";
import { t } from "../../../libs/i18n";
import type { R8CampaignBannerType } from "../utils/r8-campaign-banner";

type R8CampaignBannerProps = {
  bannerType: R8CampaignBannerType;
  onClickPrepare: () => void;
  canApply: boolean;
  config: R8CampaignConfig;
  onClickApply?: () => void;
  ageRestricted?: boolean;
  // 押下後の確認・登録画面との往復中。押しても反応がないように見えるので無効表示にする
  isPreparing?: boolean;
};

export const R8CampaignBanner = ({
  bannerType,
  onClickPrepare,
  canApply,
  config,
  onClickApply,
  ageRestricted,
  isPreparing,
}: R8CampaignBannerProps) => {
  if (bannerType === "hidden") {
    return null;
  }

  return (
    <section
      className={css({
        bg: "surface.accentPrimaryLight",
        rounded: "xl",
        p: "16px",
        display: "flex",
        flexDir: "column",
        gap: "10px",
      })}
    >
      {bannerType === "not-applied" && (
        <NotAppliedContent
          onClickPrepare={onClickPrepare}
          canApply={canApply}
          config={config}
          onClickApply={onClickApply}
          ageRestricted={ageRestricted}
          isPreparing={isPreparing}
        />
      )}
      {bannerType === "applied" && <AppliedContent config={config} />}
      {bannerType === "campaign-ended" && (
        <CampaignEndedContent config={config} />
      )}
      {bannerType === "received" && <ReceivedContent config={config} />}

      {/* キャンペーン詳細リンク（詳細ページを設けない事業では aboutCampaignType が無い） */}
      {config.aboutCampaignType !== undefined && (
        <div
          className={css({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: "8px",
          })}
        >
          <Link
            to="/about-campaign"
            search={{ type: config.aboutCampaignType }}
            className={css({
              color: "text.accentPrimary",
              textDecoration: "underline",
              fontSize: "14px",
              lineHeight: "1.4",
              letterSpacing: "0.14px",
            })}
          >
            {config.detailLinkText ?? t("キャンペーン詳細")}
          </Link>
        </div>
      )}

      {/* 応募手順ボックス（未応募時かつ年齢制限なしの場合のみ表示） */}
      {bannerType === "not-applied" && !ageRestricted && (
        <ApplicationGuide config={config} />
      )}
    </section>
  );
};

const NotAppliedContent = ({
  onClickPrepare,
  canApply,
  config,
  onClickApply,
  ageRestricted,
  isPreparing,
}: {
  onClickPrepare: () => void;
  canApply: boolean;
  config: R8CampaignConfig;
  onClickApply?: () => void;
  ageRestricted?: boolean;
  isPreparing?: boolean;
}) => {
  const navigate = useNavigate();

  const handleApply = () => {
    if (onClickApply) {
      // 白石市・気仙沼市など防災登録が必要な場合
      onClickApply();
    } else {
      // 仙台市・大崎市など直接応募の場合
      navigate({
        to: "/lottery/grant/processing",
        search: { campaign: "r8AntiInflation" },
      });
    }
  };

  const isDisabledByAge = ageRestricted === true;
  const isPrepareDisabled = isDisabledByAge || canApply || isPreparing === true;
  const isApplyEnabled = !isDisabledByAge && canApply;
  const modern = config.modernButtons === true;
  const buttonArrow = modern ? <ArrowForward width={24} height={24} /> : null;
  // 準備済みの人に押せないボタンを残しても次にやることが分かりにくいだけなので出さない
  const showPrepare = !(modern && canApply);

  return (
    <>
      {/* タイトル */}
      <div
        className={css({
          display: "flex",
          alignItems: "flex-start",
          gap: "4px",
        })}
      >
        <Info
          className={css({
            color: "text.accentPrimary",
            flexShrink: 0,
            width: "24px",
            height: "24px",
          })}
        />
        <p
          className={css({
            color: "text.primary",
            fontSize: "16px",
            lineHeight: "1.5",
            letterSpacing: "0.16px",
            whiteSpace: "pre-wrap",
          })}
        >
          {config.bannerTitle}
        </p>
      </div>

      {/* 説明文 */}
      <p
        className={css({
          color: "text.secondary",
          fontSize: "14px",
          lineHeight: "1.4",
          letterSpacing: "0.14px",
        })}
      >
        {config.bannerDescription}
      </p>

      {/* 準備するボタン */}
      {showPrepare && (
        <button
          type="button"
          className={css({
            bg: isPrepareDisabled
              ? "surface.disable"
              : modern
                ? "surface.accentPrimary"
                : "surface.accentWarn",
            rounded: "xl",
            px: "20px",
            py: "12px",
            color: isPrepareDisabled && modern ? "text.disable" : "white",
            fontWeight: 600,
            fontSize: "16px",
            lineHeight: "1.25",
            letterSpacing: "0.16px",
            height: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "2px",
          })}
          onClick={onClickPrepare}
          disabled={isPrepareDisabled}
        >
          {t("準備する")}
          {buttonArrow}
        </button>
      )}

      {/* 応募するボタン */}
      <button
        type="button"
        className={css({
          bg: isApplyEnabled ? "surface.accentPrimary" : "surface.disable",
          rounded: "xl",
          px: "20px",
          py: "12px",
          color: !isApplyEnabled && modern ? "text.disable" : "white",
          fontWeight: 600,
          fontSize: "16px",
          lineHeight: "1.25",
          letterSpacing: "0.16px",
          height: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "2px",
        })}
        onClick={handleApply}
        disabled={!isApplyEnabled}
      >
        {config.applyButtonText ?? t("応募する")}
        {buttonArrow}
      </button>

      {/* 年齢制限メッセージ */}
      {isDisabledByAge && (
        <p
          className={css({
            color: "text.accentWarn",
            fontSize: "14px",
            textAlign: "center",
          })}
        >
          {t("19歳未満のため応募いただけません")}
        </p>
      )}
    </>
  );
};

const AppliedContent = ({ config }: { config: R8CampaignConfig }) => (
  <>
    {/* タイトル */}
    <div
      className={css({
        display: "flex",
        alignItems: "flex-start",
        gap: "4px",
      })}
    >
      <Info
        className={css({
          color: "text.accentPrimary",
          flexShrink: 0,
          width: "24px",
          height: "24px",
        })}
      />
      <p
        className={css({
          color: "text.primary",
          fontSize: "16px",
          lineHeight: "1.5",
          letterSpacing: "0.16px",
          whiteSpace: "pre-wrap",
        })}
      >
        {config.bannerTitle}
      </p>
    </div>

    {/* 説明文 */}
    <p
      className={css({
        color: "text.secondary",
        fontSize: "14px",
        lineHeight: "1.4",
        letterSpacing: "0.14px",
      })}
    >
      {config.bannerDescription}
    </p>

    {/* 応募するボタン（disabled） */}
    <button
      type="button"
      className={css({
        bg: "surface.disable",
        rounded: "xl",
        px: "20px",
        py: "12px",
        color: "text.disable",
        fontWeight: 600,
        fontSize: "16px",
        lineHeight: "1.25",
        letterSpacing: "0.16px",
        height: "50px",
      })}
      disabled
    >
      {config.applyButtonText ?? t("応募する")}
    </button>

    {/* 応募済み表示 */}
    <div
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
      })}
    >
      <CheckCircle
        className={css({
          color: "text.accentSuccess",
          width: "20px",
          height: "20px",
        })}
      />
      <p
        className={css({
          color: "text.accentSuccess",
          fontSize: "14px",
        })}
      >
        {config.appliedText ?? t("応募済み")}
      </p>
    </div>
  </>
);

const CampaignEndedContent = ({ config }: { config: R8CampaignConfig }) => (
  <>
    {/* タイトル */}
    <div
      className={css({
        display: "flex",
        alignItems: "flex-start",
        gap: "4px",
      })}
    >
      <Info
        className={css({
          color: "text.accentPrimary",
          flexShrink: 0,
          width: "24px",
          height: "24px",
        })}
      />
      <p
        className={css({
          color: "text.primary",
          fontSize: "16px",
          lineHeight: "1.5",
          letterSpacing: "0.16px",
          whiteSpace: "pre-wrap",
        })}
      >
        {config.bannerTitle}
      </p>
    </div>

    {/* 終了メッセージ */}
    <div
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        py: "8px",
      })}
    >
      <p
        className={css({
          color: "text.secondary",
          fontSize: "14px",
        })}
      >
        {t("上限に達したため、キャンペーンは終了しました。")}
      </p>
    </div>
  </>
);

const ReceivedContent = ({ config }: { config: R8CampaignConfig }) => (
  <>
    <div
      className={css({
        display: "flex",
        alignItems: "flex-start",
        gap: "4px",
      })}
    >
      <Info
        className={css({
          color: "text.accentPrimary",
          flexShrink: 0,
          width: "24px",
          height: "24px",
        })}
      />
      <p
        className={css({
          color: "text.primary",
          fontSize: "16px",
          lineHeight: "1.5",
          letterSpacing: "0.16px",
          whiteSpace: "pre-wrap",
        })}
      >
        {config.bannerTitle}
      </p>
    </div>

    <p
      className={css({
        color: "text.secondary",
        fontSize: "14px",
        lineHeight: "1.4",
        letterSpacing: "0.14px",
      })}
    >
      {config.receivedText}
    </p>
  </>
);

const ApplicationGuide = ({ config }: { config: R8CampaignConfig }) => (
  <div
    className={css({
      bg: "surface.primary",
      rounded: "lg",
      p: "12px",
      display: "flex",
      flexDir: "column",
      gap: "8px",
      fontSize: "14px",
      lineHeight: "1.4",
    })}
  >
    <p
      className={css({
        fontWeight: 600,
        color: "text.primary",
      })}
    >
      {config.guideTitle ?? t("応募手順")}
    </p>
    <p
      className={css({
        color: "text.secondary",
        whiteSpace: "pre-wrap",
      })}
    >
      {config.guideSteps}
    </p>
    {!config.hideDeadlineGuide && (
      <>
        <p
          className={css({
            color: "text.secondary",
          })}
        >
          ■{config.guideReceiveLabel ?? t("ポイント受取期限")}
          <br />　{config.guideReceiveDeadline}
        </p>
        <p
          className={css({
            color: "text.secondary",
          })}
        >
          ■{config.guideUseLabel ?? t("ポイント使用期限")}
          <br />　{config.guideUseDeadline}
        </p>
      </>
    )}
    <p
      className={css({
        fontWeight: 600,
        color: "text.primary",
      })}
    >
      {t("ご注意事項")}
    </p>
    <p
      className={css({
        color: "text.secondary",
        whiteSpace: "pre-wrap",
      })}
    >
      {config.guideNotes}
    </p>
  </div>
);
